import {nowMs} from './data/clock'

/**
 * The module has no real rate card — every amount is a flat ₹68 per 0.5 kg
 * slab, zone-blind — so this wording accompanies the figure wherever there is
 * room to STATE it.
 *
 * It is deliberately NOT on the ledger grid's Excess columns any more. There it
 * could only be a tooltip, which meant ten identical hover targets down the
 * page and nothing at all for keyboard or touch users. A disclaimer nobody can
 * reach is not a disclaimer. It now lives where a merchant is actually deciding
 * — as visible prose in the drawer beside the same figure (RecordSections.js)
 * and in the evidence studio — rather than as decoration on every row.
 */
export const EXCESS_NOTE = 'Indicative only — flat ₹68 per 0.5 kg slab, zone-blind. Not a real rate-card calculation.'

export function fmtCurrency(n) {
  return '₹' + Math.round(n || 0).toLocaleString('en-IN')
}

export function fmtKg(n) {
  return (Number(n) || 0).toFixed(2) + ' kg'
}

/**
 * A courier-reported weight, or a dash when the courier did not report it. The MIS's dead and
 * volumetric columns are optional, and a missing figure printed as "0.00 kg" reads as a real
 * zero-gram reading.
 */
export function fmtKgOrDash(n) {
  return n == null || n === '' || isNaN(Number(n)) ? '—' : fmtKg(n)
}

/**
 * A parcel's dimensions as text, or null when we do not actually have them.
 *
 * Legacy discrepancies arrive either with no dims object at all or — just as often — with
 * {l: 0, b: 0, h: 0}. Those are the same fact written two ways: nothing was captured. A parcel
 * cannot have a zero side, so "0×0×0 cm" states a measurement that was never taken, sitting beside
 * real ones in a table people use to argue money with couriers. Every surface that shows a carton
 * goes through here so the grid, the drawer, the SKU table and the export cannot disagree about
 * which records have dimensions. `sep` only sets how the triple is spaced — the drawer's cards have
 * room for "35 × 29 × 11 cm" where a grid cell does not — and never what counts as recorded.
 */
export function dimsText(d, sep = '×') {
  if (!d) return null
  const sides = [d.l, d.b, d.h]
  if (sides.some((v) => v == null || !(Number(v) > 0))) return null
  return sides.join(sep) + ' cm'
}

/** The same absence, spoken: for aria labels and the export, which read rather than render. */
export function dimsSpoken(d) {
  return dimsText(d) ? `${d.l} by ${d.b} by ${d.h} centimetres` : 'dimensions not recorded'
}

export function fmtDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  return dt.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})
}

/**
 * Read against the module clock (data/clock.js): pinned in the harness and in
 * tests so the UI never drifts, the wall clock on a live mount. Label
 * granularity: hours only inside the final 24 hours of the
 * 7-day auto-accept window; days above that (so a deadline 25 or 37 hours
 * out reads as "2 days left", not "25 hours left" / "37 hours left").
 * `level`'s thresholds (crit <=24h, warn <=72h) are untouched by this — the
 * urgency filter (state/query.js), the default sort and the 48h KPI all read
 * `level`/`hours` directly, never the label string.
 */
export function deadline(d) {
  const dt = d instanceof Date ? d : new Date(d)
  const ms = dt.getTime() - nowMs()
  const hours = ms / 3600000
  if (hours <= 0) return {hours: 0, label: 'Past due', level: 'crit'}
  let label
  // Strictly INSIDE the final day, not "at or inside it". At exactly 24 hours
  // out the honest reading is "1 day left"; "24 hours left" is the same instant
  // described in the unit the merchant has already stopped needing. `level`
  // keeps its own <= 24 threshold — the urgency band and the wording answer
  // different questions, and the filter, sort and 48h KPI all read `level`.
  if (hours < 24) {
    const h = Math.round(hours)
    label = h + (h === 1 ? ' hour left' : ' hours left')
  } else {
    const days = Math.floor(hours / 24)
    label = days + (days === 1 ? ' day left' : ' days left')
  }
  return {hours, label, level: hours <= 24 ? 'crit' : hours <= 72 ? 'warn' : 'ok'}
}
